/**
 * MANAGEMENT (14) · ParamTypes i18n — the manage-screen strings (FR default + AR-RTL), ADDITIVE to the
 * shell `messages.ts`. Registered idempotently in its own `registerParamtypesMessages()` (the screen
 * calls it at module-load, mirroring how `messages.ts` registers the shell namespace). `registerMessages`
 * MERGES, so these keys layer onto the already-registered `mgmt.*` set without clobbering it. The shell
 * already owns `mgmt.paramtypes.title` / `.subtitle`; we only ADD the new authoring keys here.
 *
 * Namespacing: everything under `mgmt.paramtypes.*`. Codes / versions / disciplines stay LTR via the
 * `.nv-ltr-island` class in the screen (not the strings). Missing AR key → FR → raw key (never a crash).
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // ── toolbar / actions ──────────────────────────────────────────────────────────
  "mgmt.paramtypes.search": "Rechercher (code, libellé, discipline)…",
  "mgmt.paramtypes.filter.statut": "Statut",
  "mgmt.paramtypes.filter.statut.all": "Tous les statuts",
  "mgmt.paramtypes.filter.discipline": "Filtrer par discipline (ex. BIO)",
  "mgmt.paramtypes.insight.title": "Synthèse du catalogue (page courante)",
  "mgmt.paramtypes.insight.byType": "Par type de résultat",
  "mgmt.paramtypes.insight.byType.aria": "Répartition des types de paramètres par type de résultat",
  "mgmt.paramtypes.insight.empty": "Aucun type à analyser sur cette page.",
  "mgmt.paramtypes.insight.total": "Affichés",
  "mgmt.paramtypes.insight.active": "Actifs",
  "mgmt.paramtypes.insight.retired": "Retirés",
  "mgmt.paramtypes.insight.disciplines": "Disciplines",
  "mgmt.paramtypes.publish": "Publier un type",
  "mgmt.paramtypes.edit": "Rééditer",
  "mgmt.paramtypes.retire": "Retirer",
  "mgmt.paramtypes.restore": "Réactiver",

  // ── columns ─────────────────────────────────────────────────────────────────────
  "mgmt.paramtypes.col.code": "Code",
  "mgmt.paramtypes.col.libelle": "Libellé",
  "mgmt.paramtypes.col.discipline": "Discipline",
  "mgmt.paramtypes.col.type": "Type de résultat",
  "mgmt.paramtypes.col.unite": "Unité",
  "mgmt.paramtypes.col.version": "Version",
  "mgmt.paramtypes.col.statut": "Statut",
  "mgmt.paramtypes.col.actions": "Actions",

  // ── statut + type enums ─────────────────────────────────────────────────────────
  "mgmt.paramtypes.statut.actif": "Actif",
  "mgmt.paramtypes.statut.retire": "Retiré",
  "mgmt.paramtypes.type.numerique": "Numérique",
  "mgmt.paramtypes.type.qualitatif": "Qualitatif",
  "mgmt.paramtypes.type.texte": "Texte",

  // ── empty / states ──────────────────────────────────────────────────────────────
  "mgmt.paramtypes.empty.title": "Catalogue vide",
  "mgmt.paramtypes.empty.desc": "Aucun type d'analyse publié pour le moment.",
  "mgmt.paramtypes.empty.search": "Aucun type ne correspond à votre recherche.",

  // ── form modal ──────────────────────────────────────────────────────────────────
  "mgmt.paramtypes.modal.title.new": "Publier un type d'analyse",
  "mgmt.paramtypes.modal.title.edit": "Rééditer {code}",
  "mgmt.paramtypes.modal.code": "Code (taxonomie)",
  "mgmt.paramtypes.modal.code.hint": "Identifiant immuable, alphanumérique avec « . », « _ » ou « - ».",
  "mgmt.paramtypes.modal.libelle": "Libellé",
  "mgmt.paramtypes.modal.discipline": "Discipline (routage)",
  "mgmt.paramtypes.modal.discipline.hint": "Code de discipline CONV-9, ex. BIO, HEM, IMM.",
  "mgmt.paramtypes.modal.type": "Type de résultat",
  "mgmt.paramtypes.modal.unite": "Unité",
  "mgmt.paramtypes.modal.unite.placeholder": "ex. g/L (facultatif)",
  "mgmt.paramtypes.modal.methode": "Classe de méthode",
  "mgmt.paramtypes.modal.methode.placeholder": "Famille de méthode (facultatif)",
  "mgmt.paramtypes.modal.submit.new": "Publier",
  "mgmt.paramtypes.modal.submit.edit": "Rééditer",
  "mgmt.paramtypes.modal.cancel": "Annuler",
  "mgmt.paramtypes.modal.edit.note":
    "Une réédition publie une nouvelle version du même code — les laboratoires la synchronisent.",

  // ── retire / restore confirm ────────────────────────────────────────────────────
  "mgmt.paramtypes.confirm.retire.title": "Retirer {code} ?",
  "mgmt.paramtypes.confirm.retire.desc":
    "Le type passe en « retiré » : il bloque toute nouvelle sélection par les laboratoires, mais la ligne et son historique de synchronisation sont conservés.",
  "mgmt.paramtypes.confirm.retire.submit": "Retirer",
  "mgmt.paramtypes.confirm.restore.title": "Réactiver {code} ?",
  "mgmt.paramtypes.confirm.restore.desc":
    "Le type repasse en « actif » et redevient sélectionnable par les laboratoires.",
  "mgmt.paramtypes.confirm.restore.submit": "Réactiver",

  // ── validation + toasts ─────────────────────────────────────────────────────────
  "mgmt.paramtypes.field.required": "Ce champ est obligatoire.",
  "mgmt.paramtypes.field.code.invalid":
    "Code invalide : alphanumérique, « . », « _ » ou « - » uniquement (sans espace).",
  "mgmt.paramtypes.toast.published": "Type publié.",
  "mgmt.paramtypes.toast.reissued": "Type réédité.",
  "mgmt.paramtypes.toast.retired": "Type retiré.",
  "mgmt.paramtypes.toast.restored": "Type réactivé.",
  "mgmt.paramtypes.toast.error": "L'opération a échoué.",
};

const AR: Record<string, string> = {
  // ── toolbar / actions ──────────────────────────────────────────────────────────
  "mgmt.paramtypes.search": "بحث (الرمز، التسمية، الاختصاص)…",
  "mgmt.paramtypes.filter.statut": "الحالة",
  "mgmt.paramtypes.filter.statut.all": "كل الحالات",
  "mgmt.paramtypes.filter.discipline": "تصفية حسب الاختصاص (مثال BIO)",
  "mgmt.paramtypes.insight.title": "ملخص الكتالوج (الصفحة الحالية)",
  "mgmt.paramtypes.insight.byType": "حسب نوع النتيجة",
  "mgmt.paramtypes.insight.byType.aria": "توزيع أنواع المعايير حسب نوع النتيجة",
  "mgmt.paramtypes.insight.empty": "لا توجد أنواع للتحليل في هذه الصفحة.",
  "mgmt.paramtypes.insight.total": "المعروضة",
  "mgmt.paramtypes.insight.active": "النشطة",
  "mgmt.paramtypes.insight.retired": "المسحوبة",
  "mgmt.paramtypes.insight.disciplines": "الاختصاصات",
  "mgmt.paramtypes.publish": "نشر نوع",
  "mgmt.paramtypes.edit": "إعادة الإصدار",
  "mgmt.paramtypes.retire": "سحب",
  "mgmt.paramtypes.restore": "إعادة التفعيل",

  // ── columns ─────────────────────────────────────────────────────────────────────
  "mgmt.paramtypes.col.code": "الرمز",
  "mgmt.paramtypes.col.libelle": "التسمية",
  "mgmt.paramtypes.col.discipline": "الاختصاص",
  "mgmt.paramtypes.col.type": "نوع النتيجة",
  "mgmt.paramtypes.col.unite": "الوحدة",
  "mgmt.paramtypes.col.version": "الإصدار",
  "mgmt.paramtypes.col.statut": "الحالة",
  "mgmt.paramtypes.col.actions": "إجراءات",

  // ── statut + type enums ─────────────────────────────────────────────────────────
  "mgmt.paramtypes.statut.actif": "نشط",
  "mgmt.paramtypes.statut.retire": "مسحوب",
  "mgmt.paramtypes.type.numerique": "رقمي",
  "mgmt.paramtypes.type.qualitatif": "وصفي",
  "mgmt.paramtypes.type.texte": "نصي",

  // ── empty / states ──────────────────────────────────────────────────────────────
  "mgmt.paramtypes.empty.title": "الكتالوج فارغ",
  "mgmt.paramtypes.empty.desc": "لا يوجد أي نوع تحليل منشور حتى الآن.",
  "mgmt.paramtypes.empty.search": "لا يوجد نوع مطابق لبحثك.",

  // ── form modal ──────────────────────────────────────────────────────────────────
  "mgmt.paramtypes.modal.title.new": "نشر نوع تحليل",
  "mgmt.paramtypes.modal.title.edit": "إعادة إصدار {code}",
  "mgmt.paramtypes.modal.code": "الرمز (التصنيف)",
  "mgmt.paramtypes.modal.code.hint": "معرّف ثابت، حروف وأرقام مع « . » أو « _ » أو « - ».",
  "mgmt.paramtypes.modal.libelle": "التسمية",
  "mgmt.paramtypes.modal.discipline": "الاختصاص (التوجيه)",
  "mgmt.paramtypes.modal.discipline.hint": "رمز الاختصاص CONV-9، مثل BIO، HEM، IMM.",
  "mgmt.paramtypes.modal.type": "نوع النتيجة",
  "mgmt.paramtypes.modal.unite": "الوحدة",
  "mgmt.paramtypes.modal.unite.placeholder": "مثل g/L (اختياري)",
  "mgmt.paramtypes.modal.methode": "فئة الطريقة",
  "mgmt.paramtypes.modal.methode.placeholder": "عائلة الطريقة (اختياري)",
  "mgmt.paramtypes.modal.submit.new": "نشر",
  "mgmt.paramtypes.modal.submit.edit": "إعادة الإصدار",
  "mgmt.paramtypes.modal.cancel": "إلغاء",
  "mgmt.paramtypes.modal.edit.note":
    "إعادة الإصدار تنشر نسخة جديدة من نفس الرمز — تقوم المختبرات بمزامنتها.",

  // ── retire / restore confirm ────────────────────────────────────────────────────
  "mgmt.paramtypes.confirm.retire.title": "سحب {code}؟",
  "mgmt.paramtypes.confirm.retire.desc":
    "يصبح النوع « مسحوبًا »: يمنع أي اختيار جديد من المختبرات، لكن السطر وسجل المزامنة يُحفظان.",
  "mgmt.paramtypes.confirm.retire.submit": "سحب",
  "mgmt.paramtypes.confirm.restore.title": "إعادة تفعيل {code}؟",
  "mgmt.paramtypes.confirm.restore.desc":
    "يعود النوع « نشطًا » ويصبح قابلًا للاختيار من المختبرات.",
  "mgmt.paramtypes.confirm.restore.submit": "إعادة التفعيل",

  // ── validation + toasts ─────────────────────────────────────────────────────────
  "mgmt.paramtypes.field.required": "هذا الحقل مطلوب.",
  "mgmt.paramtypes.field.code.invalid":
    "رمز غير صالح: حروف وأرقام، « . » أو « _ » أو « - » فقط (بدون مسافات).",
  "mgmt.paramtypes.toast.published": "تم نشر النوع.",
  "mgmt.paramtypes.toast.reissued": "تمت إعادة إصدار النوع.",
  "mgmt.paramtypes.toast.retired": "تم سحب النوع.",
  "mgmt.paramtypes.toast.restored": "تمت إعادة تفعيل النوع.",
  "mgmt.paramtypes.toast.error": "فشلت العملية.",
};

let registered = false;
/** Idempotently MERGE the paramtypes manage-screen strings onto the already-registered `mgmt.*` set. */
export function registerParamtypesMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
